import type { ISignedDocInbox } from 'views/signed-doc-inbox';

export type IEnvelopeAction =
	| 'completed'
	| 'needToSign'
	| 'waitingForOthers'
	| 'voided'
	| 'underReview'
	| 'configured';

/** Purposes whose envelopes are driven by an onboarding flow, not by a person. */
const ONBOARDING_PURPOSES = ['signAgreement', 'accreditation', 'multiSignAgreement'];

/**
 * What is actually true of this envelope right now — the same reading the
 * Sentbox has always made, kept in one place so the chip, the explanation line
 * and the row action can never disagree with each other.
 */
export const actionOf = (
	env: ISignedDocInbox,
	myEmail?: string
): IEnvelopeAction => {
	const recipients = env?.recipients ?? [];
	if (env?.status === 'voided') return 'voided';
	if ((env?.status as string) === 'underReview') return 'underReview';

	const done = recipients.filter(r => r.status === 'completed').length;
	const allDone = recipients.length > 0 && done === recipients.length;

	if (env?.status === 'completed') return 'completed';
	if (env?.status === 'configured' && allDone) return 'underReview';
	if (!recipients.length) return 'configured';

	const mine = recipients.find(
		r => r.email?.toLowerCase() === myEmail?.toLowerCase()
	);
	const isOnboarding = ONBOARDING_PURPOSES.includes(env?.purpose as string);

	if (!env?.signOrder) {
		if (mine?.status === 'pending' && !isOnboarding) return 'needToSign';
		return 'waitingForOthers';
	}
	const next = recipients.find(r => r.status !== 'completed');
	if (next && next.email?.toLowerCase() === myEmail?.toLowerCase() && !isOnboarding)
		return 'needToSign';
	return 'waitingForOthers';
};

export const CHIP: Record<IEnvelopeAction, { cls: string; label: string }> = {
	completed: { cls: 'ok', label: 'COMPLETED' },
	voided: { cls: 'rej', label: 'VOIDED' },
	underReview: { cls: 'rev', label: 'UNDER REVIEW' },
	waitingForOthers: { cls: 'wait', label: 'WAITING FOR OTHERS' },
	needToSign: { cls: 'strt', label: 'NEED TO SIGN' },
	configured: { cls: 'cfg', label: 'CONFIGURED' },
};

/** Left-edge colour, mirroring the sessions queue. */
export const EDGE: Record<IEnvelopeAction, string> = {
	completed: 'e-ok',
	voided: 'e-rej',
	underReview: 'e-rev',
	waitingForOthers: 'e-rev',
	needToSign: 'e-strt',
	configured: '',
};

export const TYPE_LABEL: Record<string, string> = {
	workflow: 'Multi Signer',
	selfsign: 'Self Sign',
	signAgreement: 'Onboarding',
	accreditation: 'Onboarding',
	multiSignAgreement: 'Multi Signer',
};

const pad = (n: number) => String(n).padStart(2, '0');
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export const dayOf = (v?: string | Date | null) => {
	if (!v) return '--';
	const d = new Date(v);
	if (Number.isNaN(d.getTime())) return '--';
	return `${MONTHS[d.getMonth()]} ${pad(d.getDate())}, ${d.getFullYear()}`;
};

export const timeOf = (v?: string | Date | null) => {
	if (!v) return '';
	const d = new Date(v);
	if (Number.isNaN(d.getTime())) return '';
	const h = d.getHours();
	return `${pad(h % 12 === 0 ? 12 : h % 12)}:${pad(d.getMinutes())} ${h < 12 ? 'AM' : 'PM'}`;
};

export const dayTimeOf = (v?: string | Date | null) =>
	v ? `${dayOf(v)}, ${timeOf(v)}` : '--';

export const pageCount = (env: ISignedDocInbox) =>
	(env?.documents ?? []).reduce(
		(n, d: any) => n + (d?.document?.pages?.length ?? 0),
		0
	);

const recipientName = (r: any) => r?.fullName || r?.name || r?.email || 'Recipient';

/**
 * A blocked envelope names its blocking recipient and what it is waiting on —
 * never a bare "Waiting for others". Everything here is read off the envelope
 * itself: who has not completed, whether they opened it, and whether they have
 * agreed to the electronic-record disclosure (`consent`), which is the thing
 * that silently gates Continue in the signing widget.
 */
export const explain = (
	env: ISignedDocInbox,
	action: IEnvelopeAction
): { text: string; tone: string } => {
	const recipients = env?.recipients ?? [];
	switch (action) {
		case 'completed':
			return {
				tone: 'g2',
				text: `Completed ${dayTimeOf(env.updatedAt)} — printable copy delivered to ${
					recipients.length > 1 ? 'all recipients' : recipientName(recipients[0])
				}`,
			};
		case 'voided':
			return {
				tone: 'r2x',
				text: `Packet voided${
					(env as any)?.statusMessage ? ` — “${(env as any).statusMessage}”` : ''
				}`,
			};
		case 'underReview':
			return { tone: 'b2', text: 'Signed by all recipients — awaiting compliance review' };
		case 'configured':
			return { tone: 'b2', text: 'Draft — invitations not sent' };
		case 'needToSign':
			return { tone: '', text: 'Your signature is required on this packet' };
		default: {
			const blocking = recipients.find(r => r.status !== 'completed');
			if (!blocking) return { tone: '', text: 'Waiting on a recipient' };
			const opened = !!(blocking as any)?.openedAt || !!blocking?.sentDateTime;
			const consent = (blocking as any)?.consent;
			const bits = [
				opened ? 'opened the packet' : 'has not opened the packet yet',
				consent === false ? 'disclosure consent not agreed' : '',
			].filter(Boolean);
			return {
				tone: '',
				text: `Waiting on ${recipientName(blocking)} — ${bits.join(' · ')}`,
			};
		}
	}
};
